import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { AdviseFeatureResponse, FeatureInputSupplier, RequiredFeatureCategory } from '../../../../types/advise';
import { FeatureResponsesLegend } from './FeatureResponsesLegend';
import { FlagResponse, ResponseCell } from './ResponseCell';
import { responseKey, responsesByCell } from './SupplierFeatureInput.calc';

// Set, with the suppliers sharing the rest evenly, so each supplier's column lines up from one category's table to the next.
const FEATURE_COLUMN_WIDTH = '30%';

interface FeatureResponsesProps {
  categories: RequiredFeatureCategory[];
  suppliers: FeatureInputSupplier[];
  responses: AdviseFeatureResponse[];
  // Left out for a reader, who can't flag an answer or clear its flag.
  onFlag?: FlagResponse;
}

export const FeatureResponses = ({ categories, suppliers, responses, onFlag }: FeatureResponsesProps) => {
  const cells = responsesByCell(responses);
  return (
    <>
      {categories.map((category) => (
        <Box
          key={category.key}
          className="flex flex-col gap-1">
          <Box className="flex items-baseline justify-between gap-2">
            <TextBlock weight="semibold">Feature Responses — {category.label}</TextBlock>
            <TextSpan
              size="sm"
              color="indigoCTA">
              {category.features.length} required {category.features.length === 1 ? 'feature' : 'features'}
            </TextSpan>
          </Box>
          <TableContainer>
            <Table
              size="small"
              sx={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: FEATURE_COLUMN_WIDTH }} />
                {suppliers.map((supplier) => (
                  <col key={supplier.id} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell>Feature</TableCell>
                  {suppliers.map((supplier) => (
                    <TableCell key={supplier.id}>{supplier.supplier_name}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {category.features.map((feature) => (
                  <TableRow key={feature.key}>
                    <TableCell>{feature.label}</TableCell>
                    {suppliers.map((supplier) => (
                      <TableCell
                        key={supplier.id}
                        sx={{ verticalAlign: 'top' }}>
                        <ResponseCell
                          response={cells.get(responseKey(supplier.id, feature.key))}
                          onFlag={onFlag}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
      {categories.length > 0 && (
        <FeatureResponsesLegend showFlag={!!onFlag && responses.some((response) => response.flagged !== undefined)} />
      )}
    </>
  );
};
